/**
 * Which characters of a ⌘K result to mark as the hit (Olivia, warm accent,
 * design-handoffs/2026-10-05-document-themes/REPLY-2026-10-06b-to-lilia.md §1).
 *
 * The mark only shows what the search matched, so this follows the Lilia
 * menu's own matching (`score` in Launcher.tsx, and the server's title search,
 * which is a case-insensitive substring of the whole query):
 *
 * 1. The whole query as a case-insensitive substring: every occurrence.
 * 2. Otherwise, a query of several words whose words each appear: every
 *    occurrence of each word.
 * 3. Otherwise the fuzzy fallback, the query's letters in order (the same
 *    greedy walk `score` does): the contiguous runs it landed on.
 *
 * A run under 2 characters is never marked, and a fuzzy run needs 3: a
 * two-letter run the walk happens to land on ("ec" in "architect" for
 * "check") reads as noise, not as the hit (6 Oct, after seeing it on screen).
 *
 * Pure and string-only: the runs are rendered as React text, never as HTML,
 * so a title holding `<b>` stays text.
 */

export interface HitRun {
  text: string;
  hit: boolean;
}

/** The shortest run worth marking. */
export const MIN_HIT_RUN = 2;
/** The shortest fuzzy run worth marking. */
export const MIN_FUZZY_RUN = 3;

type Range = [start: number, end: number];

function occurrences(hay: string, needle: string): Range[] {
  const out: Range[] = [];
  if (needle.length < MIN_HIT_RUN) return out;
  let from = 0;
  for (;;) {
    const at = hay.indexOf(needle, from);
    if (at < 0) return out;
    out.push([at, at + needle.length]);
    from = at + needle.length;
  }
}

/** The greedy in-order walk `score` uses, as contiguous runs of matched indices. */
function fuzzyRuns(hay: string, needle: string): Range[] {
  const runs: Range[] = [];
  let i = 0;
  for (let h = 0; h < hay.length && i < needle.length; h += 1) {
    if (hay[h] !== needle[i]) continue;
    i += 1;
    const last = runs[runs.length - 1];
    if (last && last[1] === h) last[1] = h + 1;
    else runs.push([h, h + 1]);
  }
  // Not every letter found: the row did not match by this route, so it shows nothing.
  if (i < needle.length) return [];
  return runs;
}

/** Overlapping or touching ranges merged, in order. */
function merge(ranges: Range[]): Range[] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const out: Range[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else out.push([r[0], r[1]]);
  }
  return out;
}

/** The ranges of `text` the query matched, before splitting into runs. */
export function hitRanges(text: string, query: string): Range[] {
  const needle = query.trim().toLowerCase();
  if (!text || needle.length < MIN_HIT_RUN) return [];
  // Lower-cased letter by letter, keeping any letter whose lower case changes
  // length (İ → i̇), so an index into `hay` is an index into `text`.
  const hay = Array.from(text, (c) => {
    const l = c.toLowerCase();
    return l.length === c.length ? l : c;
  }).join("");

  const whole = occurrences(hay, needle);
  if (whole.length) return whole;

  const words = needle.split(/\s+/).filter((w) => w.length >= MIN_HIT_RUN);
  if (words.length > 1) {
    const each = words.map((w) => occurrences(hay, w));
    if (each.every((r) => r.length > 0)) return merge(each.flat());
  }

  return fuzzyRuns(hay, needle).filter(([s, e]) => e - s >= MIN_FUZZY_RUN && hay.slice(s, e).trim().length >= MIN_FUZZY_RUN);
}

/** `text` split into alternating plain and hit runs; joined, they are `text`. */
export function splitHits(text: string, query: string): HitRun[] {
  const ranges = hitRanges(text, query);
  if (!ranges.length) return text ? [{ text, hit: false }] : [];
  const out: HitRun[] = [];
  let at = 0;
  for (const [s, e] of ranges) {
    if (s > at) out.push({ text: text.slice(at, s), hit: false });
    out.push({ text: text.slice(s, e), hit: true });
    at = e;
  }
  if (at < text.length) out.push({ text: text.slice(at), hit: false });
  return out;
}
